import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, Loader2, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface UsernameStepProps {
  onNext: () => void;
}

export const UsernameStep = ({ onNext }: UsernameStepProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [username, setUsername] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);

  // Load current username on mount
  useEffect(() => {
    const loadCurrentUsername = async () => {
      if (!user?.id) return;
      
      const { data } = await supabase
        .from('users')
        .select('username')
        .eq('id', user.id)
        .maybeSingle();
      
      if (data?.username) {
        setCurrentUsername(data.username);
        setUsername(data.username);
        // Validate it
        validateAndCheck(data.username);
      }
    };
    
    loadCurrentUsername();
  }, [user?.id]);

  // Validate username format (Instagram-style)
  const validateUsername = (value: string): string | null => {
    if (!value) return "Username is required";
    if (value.length < 3) return "Username must be at least 3 characters";
    if (value.length > 30) return "Username must be 30 characters or less";
    if (!/^[a-zA-Z0-9._]+$/.test(value)) {
      return "Only letters, numbers, underscores, and periods allowed";
    }
    if (/^[._]/.test(value) || /[._]$/.test(value)) {
      return "Cannot start or end with period or underscore";
    }
    if (/\.\./.test(value) || /__/.test(value)) {
      return "Cannot have consecutive periods or underscores";
    }
    return null;
  };

  const validateAndCheck = async (value: string) => {
    const error = validateUsername(value);
    setValidationError(error);
    
    if (error) {
      setIsAvailable(null);
      return;
    }

    // If it's their current username, it's available
    if (value.toLowerCase() === currentUsername?.toLowerCase()) {
      setIsAvailable(true);
      return;
    }

    setIsChecking(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('check_username_available', {
        check_username: value.toLowerCase(),
        exclude_user_id: user?.id
      });
      
      if (rpcError) throw rpcError;
      setIsAvailable(data);
    } catch (error) {
      console.error('Error checking username:', error);
      setIsAvailable(null);
    } finally {
      setIsChecking(false);
    }
  };

  // Debounced check
  useEffect(() => {
    if (!username) return;
    
    const timer = setTimeout(() => {
      validateAndCheck(username);
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sanitize input - only allow valid characters
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, '');
    setUsername(value);
    setIsAvailable(null);
  };

  const handleSave = async () => {
    if (!user?.id || !isAvailable) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ username: username.toLowerCase() })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Username saved",
        description: `Your username is @${username}`,
      });
      
      onNext();
    } catch (error) {
      console.error('Error saving username:', error);
      toast({
        title: "Error",
        description: "Failed to save username. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isValid = username && !validationError && isAvailable === true;

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">Choose Your Username</CardTitle>
        <CardDescription>
          This is how other users will find and recognize you on Airing Down.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="username">Username</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
            <Input
              id="username"
              value={username}
              onChange={handleUsernameChange}
              placeholder="yourname"
              className="pl-8 pr-10"
              maxLength={30}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isChecking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
              {!isChecking && isAvailable === true && <Check className="w-4 h-4 text-green-500" />}
              {!isChecking && isAvailable === false && <X className="w-4 h-4 text-destructive" />}
            </div>
          </div>
          
          {/* Validation feedback */}
          {validationError && (
            <p className="text-sm text-destructive">{validationError}</p>
          )}
          {!validationError && isAvailable === false && (
            <p className="text-sm text-destructive">Username is already taken</p>
          )}
          {!validationError && isAvailable === true && (
            <p className="text-sm text-green-600">Username is available!</p>
          )}
        </div>

        <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
          <p className="font-medium mb-2">Username guidelines:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>3-30 characters long</li>
            <li>Letters, numbers, underscores, and periods only</li>
            <li>No spaces or special characters</li>
          </ul>
        </div>

        <Button 
          onClick={handleSave}
          disabled={!isValid || isSaving}
          className="w-full"
          size="lg"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
